import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'

export interface Command {
  id: string
  label: string
  icon: string
  category: string
  action: () => void
  keywords?: string[]
}

const isOpen = ref(false)
const searchQuery = ref('')

export function useCommandPalette() {
  const router = useRouter()

  const commands: Command[] = [
    // Navigation — PRINCIPAL
    { id: 'nav-dashboard', label: 'Ir para Dashboard', icon: 'pi pi-home', category: 'Navegação', action: () => router.push('/manager'), keywords: ['home', 'inicio', 'painel'] },
    { id: 'nav-instances', label: 'Ir para Instâncias', icon: 'pi pi-server', category: 'Navegação', action: () => router.push('/manager/instances'), keywords: ['whatsapp', 'conexão', 'servidor'] },
    { id: 'nav-inbox', label: 'Ir para Caixa de Entrada', icon: 'pi pi-inbox', category: 'Navegação', action: () => router.push('/manager/inbox'), keywords: ['mensagens', 'conversas', 'chat', 'inbox', 'atendimento'] },
    { id: 'nav-groups', label: 'Ir para Grupos', icon: 'pi pi-users', category: 'Navegação', action: () => router.push('/manager/groups'), keywords: ['grupo'] },
    { id: 'nav-send', label: 'Enviar Teste', icon: 'pi pi-send', category: 'Navegação', action: () => router.push('/manager/send'), keywords: ['texto', 'mídia', 'mensagem'] },
    // Navigation — MONITOR
    { id: 'nav-events', label: 'Monitor de Eventos', icon: 'pi pi-bolt', category: 'Navegação', action: () => router.push('/manager/events'), keywords: ['websocket', 'real-time', 'eventos'] },
    { id: 'nav-health', label: 'Saúde do Sistema', icon: 'pi pi-heart', category: 'Navegação', action: () => router.push('/manager/health'), keywords: ['status', 'postgres', 'redis', 'health'] },
    { id: 'nav-logs', label: 'Ver Logs', icon: 'pi pi-file', category: 'Navegação', action: () => router.push('/manager/logs'), keywords: ['error', 'debug', 'log'] },
    // Navigation — ADMIN
    { id: 'nav-failover', label: 'Failover', icon: 'pi pi-shield', category: 'Navegação', action: () => router.push('/manager/failover'), keywords: ['redundância', 'alta disponibilidade'] },
    { id: 'nav-alerts', label: 'Alertas', icon: 'pi pi-bell', category: 'Navegação', action: () => router.push('/manager/alerts'), keywords: ['notificação', 'alerta'] },
    { id: 'nav-voip', label: 'VoIP', icon: 'pi pi-phone', category: 'Navegação', action: () => router.push('/manager/voip'), keywords: ['chamada', 'ligação', 'voz'] },
    { id: 'nav-chatwoot', label: 'Chatwoot', icon: 'pi pi-headphones', category: 'Navegação', action: () => router.push('/manager/chatwoot'), keywords: ['suporte', 'atendimento'] },
    { id: 'nav-communities', label: 'Comunidades', icon: 'pi pi-building', category: 'Navegação', action: () => router.push('/manager/communities'), keywords: ['comunidade'] },
    // Actions
    { id: 'act-new-instance', label: 'Criar Nova Instância', icon: 'pi pi-plus', category: 'Ações', action: () => router.push('/manager/instances'), keywords: ['nova', 'adicionar', 'criar'] },
    { id: 'act-send-text', label: 'Enviar Texto Rápido', icon: 'pi pi-comment', category: 'Ações', action: () => router.push('/manager/send'), keywords: ['mensagem', 'texto'] },
    { id: 'act-send-media', label: 'Enviar Mídia', icon: 'pi pi-image', category: 'Ações', action: () => router.push('/manager/send'), keywords: ['foto', 'vídeo', 'imagem', 'arquivo'] },
    // Tools
    { id: 'tool-swagger', label: 'Abrir API Docs (Swagger)', icon: 'pi pi-book', category: 'Ferramentas', action: () => window.open('/swagger/index.html', '_blank') },
  ]

  const filteredCommands = computed(() => {
    if (!searchQuery.value) return commands
    const q = searchQuery.value.toLowerCase()
    return commands.filter(cmd =>
      cmd.label.toLowerCase().includes(q) ||
      cmd.category.toLowerCase().includes(q) ||
      cmd.keywords?.some(k => k.includes(q))
    )
  })

  const groupedCommands = computed(() => {
    const groups: Record<string, Command[]> = {}
    for (const cmd of filteredCommands.value) {
      if (!groups[cmd.category]) groups[cmd.category] = []
      groups[cmd.category].push(cmd)
    }
    return groups
  })

  function open() {
    isOpen.value = true
    searchQuery.value = ''
  }

  function close() {
    isOpen.value = false
  }

  function toggle() {
    isOpen.value ? close() : open()
  }

  function execute(cmd: Command) {
    cmd.action()
    close()
  }

  return { isOpen, searchQuery, filteredCommands, groupedCommands, open, close, toggle, execute }
}
